<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.receipt.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="sm" type="default" @click="$router.back()">
            <i class="fa fa-arrow-left"></i> {{ $t("back") || "Back" }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- Header Card -->
      <card class="mb-4" v-if="receipt">
        <div class="row">
          <div class="col-md-3">
            <small class="text-muted d-block">{{ $t("modern_trade.company.name") }}</small>
            <strong>{{ receipt.company_name || receipt.company_id }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">{{ $t("modern_trade.receipt.po_number") }}</small>
            <strong>{{ receipt.po_number }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">{{ $t("modern_trade.receipt.status") }}</small>
            <span class="badge" :class="statusClass(receipt.status)">{{ statusLabel(receipt.status) }}</span>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">{{ $t("modern_trade.receipt.received_at") }}</small>
            <span>{{ formatDate(receipt.received_at) }}</span>
          </div>
          <div class="col-md-3 text-right">
            <base-button size="sm" type="info" :loading="downloadingPo" @click="downloadPoFile">
              <i class="fa fa-download"></i> {{ $t("modern_trade.receipt.download_po") }}
            </base-button>
            <base-button
              v-if="isEditModernTrade && receipt.status === 1"
              size="sm"
              type="danger"
              :loading="deletingReceipt"
              @click="handleDeleteReceipt"
            >
              <i class="fa fa-trash"></i>
            </base-button>
          </div>
        </div>
        <div class="row mt-2" v-if="receipt.note">
          <div class="col-12">
            <small class="text-muted">{{ $t("modern_trade.receipt.note") }}: {{ receipt.note }}</small>
          </div>
        </div>
      </card>

      <!-- Import Wizard (only when draft) -->
      <card v-if="receipt && receipt.status === 1 && isEditModernTrade" class="mb-4">
        <template slot="header">
          <h3 class="mb-0">{{ $t("modern_trade.receipt.import_items") }}</h3>
        </template>

        <!-- Step 1: Pick file -->
        <div v-if="wizardStep === 1">
          <div class="row align-items-end">
            <div class="col-md-6">
              <label class="form-control-label">{{ $t("modern_trade.import.pick_file") }} (.xls / .xlsx)</label>
              <input
                ref="importFileInput"
                type="file"
                accept=".xls,.xlsx"
                class="form-control"
                @change="onImportFileChange"
              />
            </div>
            <div class="col-md-6 pb-1">
              <base-button type="primary" :loading="validating" :disabled="!importFile" @click="doValidate">
                <i class="fa fa-check"></i> {{ $t("modern_trade.import.validate") }}
              </base-button>
              <base-button type="default" @click="downloadTemplate">
                <i class="fa fa-download"></i> {{ $t("modern_trade.import.template") }}
              </base-button>
            </div>
          </div>
        </div>

        <!-- Step 2: Preview -->
        <div v-if="wizardStep === 2">
          <!-- Summary chips -->
          <div class="mb-3">
            <span class="badge badge-secondary mr-2">{{ $t("modern_trade.import.summary_total") }}: {{ batch.row_total }}</span>
            <span class="badge badge-success mr-2">{{ $t("modern_trade.import.summary_ok") }}: {{ batch.row_ok }}</span>
            <span class="badge badge-danger mr-2">{{ $t("modern_trade.import.summary_error") }}: {{ batch.row_error }}</span>
          </div>

          <!-- Row preview table -->
          <div v-loading="loadingRows">
            <el-table :data="previewRows" header-row-class-name="thead-light" size="small">
              <el-table-column :label="$t('modern_trade.import.row_no')" prop="row_no" width="60" align="center"></el-table-column>
              <el-table-column label="Tax ID" prop="tax_id" min-width="130"></el-table-column>
              <el-table-column label="Product" min-width="150">
                <template slot-scope="{ row }">
                  {{ row.product_name || row.product_code }}
                </template>
              </el-table-column>
              <el-table-column label="Lot No." prop="lot_no" min-width="100"></el-table-column>
              <el-table-column :label="$t('modern_trade.stock.expired_date')" prop="expired_date" min-width="110"></el-table-column>
              <el-table-column :label="$t('modern_trade.stock.qty')" prop="qty" min-width="70" align="right"></el-table-column>
              <el-table-column :label="$t('modern_trade.import.status_ok')" min-width="90" align="center">
                <template slot-scope="{ row }">
                  <span class="badge" :class="row.status === 1 ? 'badge-success' : 'badge-danger'">
                    {{ row.status === 1 ? $t("modern_trade.import.status_ok") : $t("modern_trade.import.status_error") }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.import.error_message')" min-width="200">
                <template slot-scope="{ row }">
                  <span v-if="row.error_msg" class="text-danger">{{ translateError(row.error_msg) }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="mt-3 d-flex align-items-center">
            <base-button type="secondary" @click="resetWizard">
              <i class="fa fa-arrow-left"></i> {{ $t("modern_trade.import.cancel") }}
            </base-button>
            <base-button
              v-if="batch.row_error > 0"
              type="default"
              :loading="downloadingReport"
              @click="downloadErrorReport"
            >
              <i class="fa fa-download"></i> {{ $t("modern_trade.import.download_report") }}
            </base-button>
            <base-button
              type="success"
              :loading="confirming"
              :disabled="batch.row_ok === 0"
              @click="doConfirm"
            >
              <i class="fa fa-check"></i> {{ $t("modern_trade.import.confirm") }}
            </base-button>
            <small v-if="batch.row_ok > 0 && batch.row_error > 0" class="text-muted ml-2">
              {{ $t("modern_trade.import.confirm_note") }} ({{ batch.row_ok }} แถว)
            </small>
          </div>
        </div>
      </card>

      <!-- Committed items table (when status = received) -->
      <card v-if="receipt && receipt.status === 2" class="mb-4">
        <template slot="header">
          <h3 class="mb-0">{{ $t("modern_trade.receipt.items") }}</h3>
        </template>
        <div v-loading="loadingItems">
          <el-table :data="committedItems" header-row-class-name="thead-light" size="small">
            <el-table-column label="Product" min-width="160">
              <template slot-scope="{ row }">{{ row.product_name || row.product_code }}</template>
            </el-table-column>
            <el-table-column label="Lot No." prop="lot_no" min-width="110"></el-table-column>
            <el-table-column :label="$t('modern_trade.stock.expired_date')" prop="expired_date" min-width="110"></el-table-column>
            <el-table-column :label="$t('modern_trade.stock.qty')" prop="qty" min-width="80" align="right"></el-table-column>
          </el-table>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn } from "element-ui";
import { utils, writeFile } from "xlsx";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtReceiptDetail",
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  computed: {
    ...mapGetters({
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
  },
  data() {
    return {
      receipt: null,
      loadingReceipt: false,
      downloadingPo: false,
      deletingReceipt: false,
      wizardStep: 1,
      importFile: null,
      validating: false,
      confirming: false,
      loadingRows: false,
      loadingItems: false,
      downloadingReport: false,
      batch: { id: null, row_total: 0, row_ok: 0, row_error: 0 },
      previewRows: [],
      committedItems: [],
    };
  },
  mounted() {
    this.loadReceipt();
  },
  beforeDestroy() {
    this.cancelPendingBatch();
  },
  beforeRouteLeave(to, from, next) {
    this.cancelPendingBatch();
    next();
  },
  methods: {
    async loadReceipt() {
      this.loadingReceipt = true;
      this.loadingItems = true;
      try {
        const res = await ModernTradeService.receiptGet(this.$route.params.id);
        // BE returns Data[0] = { receipt, company, items, batch } (envelope).
        const d = (res.data && res.data.Data && res.data.Data[0]) || null;
        if (d && d.receipt) {
          this.receipt = Object.assign({}, d.receipt, {
            company_name: d.company && d.company.name,
          });
          this.committedItems = d.items || [];
        } else {
          this.receipt = null;
          this.committedItems = [];
        }
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loadingReceipt = false;
        this.loadingItems = false;
      }
    },
    async downloadPoFile() {
      this.downloadingPo = true;
      try {
        const res = await ModernTradeService.receiptPoFile(this.$route.params.id);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        if (d.url) window.open(d.url, "_blank");
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.downloadingPo = false;
      }
    },
    async handleDeleteReceipt() {
      const result = await swal.fire({
        title: this.$t("modern_trade.receipt.delete_draft_confirm"),
        icon: "warning",
        showCancelButton: true,
      });
      if (!result.value) return;
      this.deletingReceipt = true;
      try {
        await ModernTradeService.receiptDelete(this.$route.params.id);
        this.$router.push("/modern-trade/receipts");
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.deletingReceipt = false;
      }
    },
    onImportFileChange(e) {
      this.importFile = e.target.files[0] || null;
    },
    async doValidate() {
      if (!this.importFile) return;
      this.validating = true;
      try {
        const fd = new FormData();
        fd.append("file", this.importFile);
        const res = await ModernTradeService.receiptImportValidate(this.$route.params.id, fd);
        const d = (res.data && res.data.Datum) || {};
        this.batch = {
          id: d.batch_id,
          row_total: d.row_total || 0,
          row_ok: d.row_ok || 0,
          row_error: d.row_error || 0,
        };
        this.previewRows = d.rows || [];
        this.wizardStep = 2;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.validating = false;
      }
    },
    async doConfirm() {
      this.confirming = true;
      try {
        await ModernTradeService.receiptImportConfirm(this.$route.params.id, { batch_id: this.batch.id });
        // Batch is now committed — clear the id so resetWizard/leave guards
        // don't attempt to cancel an already-committed batch.
        this.batch.id = null;
        this.resetWizard();
        this.loadReceipt();
        swal.fire({ title: this.$t("modern_trade.import.import_success"), icon: "success", timer: 1500, showConfirmButton: false });
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.confirming = false;
      }
    },
    async downloadErrorReport() {
      this.downloadingReport = true;
      try {
        const res = await ModernTradeService.batchReportExport(this.batch.id);
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `receipt_import_report_${this.batch.id}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.downloadingReport = false;
      }
    },
    downloadTemplate() {
      // Generate a real .xlsx template. The BE import parser only accepts
      // .xls/.xlsx, so a CSV blob could never be re-uploaded. Uses the bundled
      // `xlsx` dependency (already used elsewhere) — no new deps.
      const rows = [
        ["tax_id (เลขผู้เสียภาษี)", "product_code (รหัสสินค้า)", "lot_no (Lot)", "expired_date (วันหมดอายุ)", "qty (จำนวน)"],
        ["1234567890123", "PROD001", "LOT-2024-001", "2025-12-31", "100"],
      ];
      const ws = utils.aoa_to_sheet(rows);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "receipt_import");
      writeFile(wb, "receipt_import_template.xlsx");
    },
    resetWizard() {
      // Explicit Cancel/reset: release the server-side validated batch so the
      // SHA-256 dedupe doesn't block re-uploading the same file forever.
      this.cancelPendingBatch();
      this.wizardStep = 1;
      this.importFile = null;
      this.batch = { id: null, row_total: 0, row_ok: 0, row_error: 0 };
      this.previewRows = [];
      if (this.$refs.importFileInput) this.$refs.importFileInput.value = "";
    },
    cancelPendingBatch() {
      // Cancel an uncommitted validated batch (fire-and-forget). Nulls the id
      // first so it is idempotent across resetWizard/beforeDestroy/route-leave.
      const bid = this.batch && this.batch.id;
      if (!bid) return;
      this.batch.id = null;
      ModernTradeService.batchCancel(bid).catch(() => {});
    },
    translateError(msg) {
      // Map server error_msg keys to i18n
      const map = {
        expired_on_receive: this.$t("modern_trade.validation.expired_on_receive"),
        shelf_life_too_short: this.$t("modern_trade.validation.shelf_life_too_short"),
        lot_expiry_mismatch: this.$t("modern_trade.validation.lot_expiry_mismatch"),
        unknown_product: this.$t("modern_trade.validation.unknown_product"),
        tax_id_mismatch: this.$t("modern_trade.validation.tax_id_mismatch"),
        company_inactive: this.$t("modern_trade.validation.company_inactive"),
        bad_date: this.$t("modern_trade.validation.bad_date"),
        bad_qty: this.$t("modern_trade.validation.bad_qty"),
        duplicate_row: this.$t("modern_trade.validation.duplicate_row"),
        unknown_tax_id: this.$t("modern_trade.validation.unknown_tax_id"),
      };
      return map[msg] || msg;
    },
    statusLabel(status) {
      switch (status) {
        case 1: return this.$t("modern_trade.receipt.status_draft");
        case 2: return this.$t("modern_trade.receipt.status_received");
        case 9: return this.$t("modern_trade.receipt.status_canceled");
        default: return "-";
      }
    },
    statusClass(status) {
      switch (status) {
        case 1: return "badge-warning";
        case 2: return "badge-success";
        case 9: return "badge-secondary";
        default: return "badge-secondary";
      }
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    },
  },
};
</script>
